import { listPlugins } from "@fuse/api/pluginApi"
import { atom } from "recoil"

export interface Integration {
  id: string
  name: string
  description: string
  icon: string
  category:
    | "questionnaires"
    | "rsvp_notes"
    | "shared_playlist"
    | "shared_album"
    | "custom"
}

// Extended interface for custom plugins from Firestore
export interface PluginIntegration extends Integration {
  pluginId: string
  manifest?: {
    name: string
    description: string
    version: string
    author: string
    searchTags?: string[]
  }
  isCustom: boolean
}

// Default known modules for fast loading
export const defaultIntegrations: Integration[] = [
  // {
  //   id: "questionnaires",
  //   name: "Questionnaires",
  //   description: "Create custom forms for your event attendees",
  //   icon: "form",
  //   category: "questionnaires",
  // },
  // {
  //   id: "rsvp_notes",
  //   name: "RSVP notes",
  //   description: "Add special notes for your event RSVP",
  //   icon: "note",
  //   category: "rsvp_notes",
  // },
  // {
  //   id: "shared_playlist",
  //   name: "Shared playlist",
  //   description: "Create collaborative playlists for your event",
  //   icon: "music",
  //   category: "shared_playlist",
  // },
  // {
  //   id: "shared_album",
  //   name: "Shared album",
  //   description: "Share photos with event attendees",
  //   icon: "photo",
  //   category: "shared_album",
  // },
]

// State for all integrations
export const integrationsState = atom<Integration[]>({
  key: "integrationsState",
  default: defaultIntegrations,
})

// State for custom plugins loaded state
export type PluginsLoadState = "unloaded" | "loading" | "loaded"

export const pluginsLoadedState = atom<PluginsLoadState>({
  key: "pluginsLoadedState",
  default: "unloaded",
})

// State for filtered integrations (based on search or category)
export const filteredIntegrationsState = atom<Integration[]>({
  key: "filteredIntegrationsState",
  default: defaultIntegrations,
})

// State for loading status
export const integrationsLoadingState = atom<boolean>({
  key: "integrationsLoadingState",
  default: false,
})

// State for search query
export const integrationsSearchQueryState = atom<string>({
  key: "integrationsSearchQueryState",
  default: "",
})

// Function to fetch custom plugins from backend API
export const fetchCustomPlugins = async (): Promise<PluginIntegration[]> => {
  try {
    console.log("[fetchCustomPlugins] Starting to fetch plugins from API")

    // Use the listPlugins API function with filter for integrations
    const response = await listPlugins({
      // isEnabled: true, // Only get enabled plugins
      // tags: ["integration"], // Filter to get only integrations
    })

    console.log("[fetchCustomPlugins] API response received:", {
      success: response.success,
      error: response.error,
      dataExists: !!response.data,
    })

    // Enhanced debugging - log the complete structure
    if (response.data) {
      console.log(
        "[fetchCustomPlugins] Full response data structure:",
        JSON.stringify(
          {
            pluginsCount: response.data?.plugins?.length || 0,
            paginationInfo: response.data?.pagination,
            firstPluginStructure: response.data?.plugins?.length
              ? {
                  id: response.data?.plugins[0].id,
                  hasManifest: !!response.data?.plugins[0].manifest,
                  hasCode: !!response.data?.plugins[0].code,
                  hasFoldersProperty: !!(response.data?.plugins[0] as any)
                    .folders,
                  properties: Object.keys(response.data?.plugins[0]),
                }
              : "No plugins",
          },
          null,
          2,
        ),
      )
    }

    if (!response.success) {
      console.log(
        "[fetchCustomPlugins] Failed to fetch plugins from API:",
        response.error || "No error details provided",
      )
      return []
    }

    // Type assertion for the response data
    const responseData = response.data as { plugins?: any[] } | undefined

    console.log("[fetchCustomPlugins] Response data structure:", {
      dataExists: !!responseData,
      pluginsExists: responseData ? Array.isArray(responseData.plugins) : false,
      pluginsCount:
        responseData && responseData.plugins ? responseData.plugins.length : 0,
    })

    if (
      !responseData ||
      !Array.isArray(responseData.plugins) ||
      responseData.plugins.length === 0
    ) {
      console.log("[fetchCustomPlugins] No custom plugins found via API")
      return []
    }

    // Log details about each plugin to help with debugging
    responseData.plugins.forEach((plugin, index) => {
      console.log(
        `[fetchCustomPlugins] Plugin ${index + 1}/${responseData.plugins.length}:`,
        {
          id: plugin.id || "missing-id",
          hasManifest: !!plugin.manifest,
          name: plugin.manifest?.name || "unnamed",
          description: plugin.manifest?.description
            ? plugin.manifest.description.length > 30
              ? plugin.manifest.description.substring(0, 30) + "..."
              : plugin.manifest.description
            : "no description",
          validForIntegration: !!(
            plugin.manifest?.name && plugin.manifest?.description
          ),
        },
      )
    })

    // Map API response to PluginIntegration objects
    const customPlugins: PluginIntegration[] = []

    responseData.plugins.forEach((plugin: any) => {
      // Only include plugins with complete data
      if (plugin.manifest?.name && plugin.manifest?.description) {
        customPlugins.push({
          id: plugin.id,
          pluginId: plugin.id,
          name: plugin.manifest.name,
          description: plugin.manifest.description,
          icon: "form", // Default icon
          category: "custom",
          isCustom: true,
          manifest: {
            name: plugin.manifest.name,
            description: plugin.manifest.description,
            version: plugin.manifest.version || "1.0.0",
            author: plugin.manifest.author || "Unknown",
            searchTags: plugin.manifest.searchTags || [],
          },
        })
      } else {
        console.warn(
          `[fetchCustomPlugins] Skipping plugin ${plugin.id} - missing name or description`,
        )
      }
    })

    console.log(
      `[fetchCustomPlugins] Successfully processed ${customPlugins.length} out of ${responseData.plugins.length} plugins`,
    )
    return customPlugins
  } catch (error) {
    console.error(
      "[fetchCustomPlugins] Exception while fetching plugins:",
      error,
    )

    // Get more detailed information about the error
    const errorDetails = {
      name: error instanceof Error ? error.name : "Unknown error type",
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : "No stack trace",
      objectType: Object.prototype.toString.call(error),
    }

    console.error("[fetchCustomPlugins] Error details:", errorDetails)

    return []
  }
}
